import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Gift, X, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CountdownPill } from "@/components/Countdown";

const SEEN = "ntv_exit_seen";
const HIDE_ON = ["/join", "/register", "/login", "/watch", "/auth/callback", "/payment/callback", "/subscribe", "/profiles"];

// One-time (per session) exit-intent modal nudging guests to claim the 24h free offer.
export const ExitIntentPopup = () => {
  const { t } = useTranslation();
  const loc = useLocation();
  const { user, subscribed } = useAuth();
  const { fromDisplay, freeAccess } = useCurrency();
  const [open, setOpen] = useState(false);

  const blocked = user !== false || subscribed || HIDE_ON.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));

  useEffect(() => {
    if (blocked) return;
    if (sessionStorage.getItem(SEEN) === "1") return;
    let armed = true;

    const trigger = () => {
      if (!armed || sessionStorage.getItem(SEEN) === "1") return;
      armed = false;
      sessionStorage.setItem(SEEN, "1");
      setOpen(true);
    };

    // Desktop: cursor leaves the top edge of the viewport.
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) trigger();
    };

    // Mobile: a decisive scroll back up after the visitor scrolled down.
    let lastY = window.scrollY;
    let wentDown = false;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastY && y > 400) wentDown = true;
      if (wentDown && y < lastY - 50 && y < 250) trigger();
      lastY = y;
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, [blocked]);

  if (!open) return null;

  const close = () => setOpen(false);

  return (
    <div data-testid="exit-popup" onClick={close}
      className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/75 backdrop-blur-sm animate-fade-in">
      <div data-testid="exit-popup-card" onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-[#121216] border border-white/10 shadow-2xl p-7 sm:p-8 text-center animate-fade-up">
        <button data-testid="exit-popup-close" onClick={close} aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full hover:bg-white/10 transition-colors">
          <X size={18} />
        </button>
        <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-gradient-to-br from-brand to-red-600 grid place-items-center shadow-lg shadow-brand/40 ring-2 ring-white/15">
          <Gift size={30} />
        </div>
        <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">{freeAccess ? t("free.exitTitle") : t("promo.exitTitle")}</h2>
        <p className="text-zinc-400 text-sm leading-relaxed mb-5">{freeAccess ? t("free.exitBody") : t("promo.exitBody", { price: fromDisplay || "R19.99" })}</p>
        {freeAccess ? (
          <div data-testid="exit-popup-free-badge" className="inline-flex items-center gap-2 mb-6 px-3.5 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-sm font-bold text-emerald-300">
            {t("free.badge")}
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 mb-6 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm">
            <Clock size={14} className="text-brand" />
            <span className="text-zinc-300">{t("promo.countdown")}</span>
            <CountdownPill className="font-bold text-white" />
          </div>
        )}
        <Link to="/register" data-testid="exit-popup-cta" onClick={close}
          className="flex items-center justify-center gap-2 w-full bg-brand hover:bg-red-600 text-white font-bold py-3.5 rounded-xl transition-colors shadow-lg shadow-brand/30">
          <Gift size={18} /> {freeAccess ? t("free.cta") : t("promo.exitCta")}
        </Link>
        <button data-testid="exit-popup-dismiss" onClick={close}
          className="mt-3 text-zinc-500 hover:text-zinc-300 text-sm transition-colors">
          {freeAccess ? t("free.exitDismiss") : t("promo.exitDismiss")}
        </button>
      </div>
    </div>
  );
};
